// Position-size helper on the trade plan: how many shares keep a stop-out to a chosen share of
// the capital the viewer types in. The capital stays in this browser (localStorage) and is never
// sent to the server.
import { useEffect, useRef, useState } from 'react';
import { NSE, positionSize } from '@planb/core';
import { useMe } from '@/lib/client/use-me';
import { Input } from '@/components/ui/input';
import { Pills } from './market-ui';

const cur = NSE.currencySymbol;
const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const RISKS = ['0.5', '1', '2'];

function stored(key: string, fallback: string): string {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
}
function keep(key: string, v: string) {
  try { localStorage.setItem(key, v); } catch { /* private mode: just not remembered */ }
}

export interface SizeCase { label: string; entry: number; riskPerShare: number }

export function PositionSize({ cases }: { cases: SizeCase[] }) {
  const [capital, setCapital] = useState(() => stored('mhq.capital', ''));
  const [risk, setRisk] = useState(() => { const r = stored('mhq.riskPct', '1'); return RISKS.includes(r) ? r : '1'; });
  // The account's default applies until a risk is picked in this browser.
  const prefRisk = useMe()?.prefs.riskPct;
  const picked = useRef(stored('mhq.riskPct', '') !== '');
  useEffect(() => { if (!picked.current && prefRisk && RISKS.includes(String(prefRisk))) setRisk(String(prefRisk)); }, [prefRisk]);
  const cap = Number(capital.replace(/[^\d.]/g, ''));
  return (
    <section aria-label="Position size" className="mt-5 border-t pt-4">
      <h3 className="mb-2 text-[13px] font-semibold">Position size</h3>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Capital {cur}
          <Input inputMode="numeric" placeholder="e.g. 500000" value={capital} className="h-8 w-32 num"
            onChange={(e) => { setCapital(e.target.value); keep('mhq.capital', e.target.value); }} aria-label="Trading capital" />
        </label>
        <Pills size="sm" label="Risk per trade" value={risk} onChange={(v) => { picked.current = true; setRisk(v); keep('mhq.riskPct', v); }}
          options={RISKS.map((r) => ({ id: r, label: `${r}%` }))} />
      </div>
      {cap > 0 ? (
        <div className="mt-2.5 grid gap-1.5">
          {cases.map((c) => {
            const s = positionSize(cap, Number(risk), c.entry, c.riskPerShare);
            return (
              <div key={c.label} className="text-[13px]">
                <div className="text-xs text-muted-foreground">{c.label}</div>
                <div className="num font-medium text-ink">
                  {s ? <>{inr.format(s.qty)} shares · {cur}{inr.format(s.atRisk)} at risk · {cur}{inr.format(s.value)}{s.capped && ' · capped by capital'}</> : 'too small for one share'}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        The number of shares at which hitting the stop loses about {risk}% of the capital you enter, without borrowing.
        Cash-market shares only: F&amp;O contracts trade in fixed lots. Your capital is kept in this browser only.
      </p>
    </section>
  );
}
